// ✅ C:\Users\MedRCM\Downloads\MedRCM\my-app\src\pages\Practice_Administrators\BusinessFinance.jsx
import React, { useEffect, useState } from "react";
import "./BusinessFinance.css";
import { CDN_BASE_URL } from "../../config/cdn";

/* =================== THEME =================== */
const theme = {
  primary: "#0f6b5e",
  accent: "#00c4b8",
  lightPanel: "#e8f6f4",
  cardBg: "#ffffff",
  textDark: "#053936",
};

/* =================== CONTACT POPUP =================== */
const ContactPopup = ({ open, onClose, pageName }) => {
  const [successOpen, setSuccessOpen] = useState(false);  // 👉 new state for success popup
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    hospital: "",
    facilityType: "",
    query: "",
  });

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // === Python Backend Submit ===
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch("http://localhost:5000/submit-form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pageName: pageName || "Business Finance Page",
          ...formData,
        }),
      });

      const result = await response.json();
      if (result.success) {
        onClose();            // closes form popup
        setSuccessOpen(true); // opens thank you popup

        setTimeout(() => {
          setSuccessOpen(false);
        }, 3000); // auto-close after 3 seconds
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <>
      {/* ---------- CONTACT FORM POPUP ---------- */}
      {open && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>Contact Us</h3>

            <form onSubmit={handleSubmit}>
              <label>Name *</label>
              <input name="name" required onChange={handleChange} />

              <label>Email *</label>
              <input type="email" name="email" required onChange={handleChange} />

              <label>Phone</label>
              <input name="phone" onChange={handleChange} />

              <label>Hospital</label>
              <input name="hospital" onChange={handleChange} />

              <label>Healthcare Facility Type *</label>
              <select name="facilityType" required onChange={handleChange}>
                <option value="">Select a facility type</option>
                <option>Hospitals</option>
                <option>Group Practice</option>
                <option>Individual Provider</option>
              </select>

              <label>Query</label>
              <textarea name="query" rows={4} onChange={handleChange}></textarea>

              <button type="submit" className="submit-btn">
                Schedule my demo
              </button>
            </form>

            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
        </div>
      )}

      {/* ---------- THANK YOU SUCCESS POPUP ---------- */}
      {successOpen && (
        <div className="modal-overlay">
          <div className="modal-box success-modal">
            <h3>Thank You!</h3>
            <p>Our specialist will contact you soon.</p>
          </div>
        </div>
      )}
    </>
  );
};

/* =================== HERO =================== */
const Hero = ({ primary, accent }) => {
  const bgUrl = `${CDN_BASE_URL}/images/businessbg.png`;

  return (
    <header
      className="bf-hero"
      style={{
        backgroundImage: `url(${bgUrl})`,
        "--bf-primary": primary,
        "--bf-accent": accent,
      }}
    >
      <div className="bf-hero-overlay" />
      <div className="bf-hero-inner">
        <h1 className="bf-hero-title">
          <span className="hero-practice">Practice</span>
          <br />
          <span className="hero-admin">Administrators</span>
        </h1>
      </div>
    </header>
  );
};

/* =================== FEATURE COMPONENT =================== */
const Feature = ({ icon, title, desc }) => (
  <div className="bf-feature-mini">
    <div className="bf-feature-icon">{icon}</div>
    <div>
      <h4 style={{ fontSize: "1.3rem", fontWeight: "700" }}>{title}</h4>
      <p>{desc}</p>
    </div>
  </div>
);

/* =================== INFO PANEL SECTION =================== */
const InfoPanel = ({ primary, openPopup }) => {
  return (
    <section className="bf-info-panel" style={{ backgroundColor: primary }}>
      <div className="bf-container">
        <div className="bf-top">
          <div className="bf-left">
            <h2 className="bf-title">
              Optimize Operations <br /> and Boost Your Revenue
            </h2>

            <button className="bf-cta-btn" onClick={openPopup}>
              Let's Talk.
            </button>
          </div>

          <div className="bf-right">
            <p className="bf-desc">
              We excel in creating customized solutions that simplify administrative workflows,
              enhance financial efficiency, and drive superior outcomes for your organization.
              With our expertise, you can focus on delivering quality patient care while we
              handle the complexities of revenue cycle management.
            </p>
          </div>
        </div>

        <div className="bf-feature-row">
          <Feature icon="⚙️" title="System Efficiency" desc="Managing claims, contracts, and clearinghouse systems can be time-consuming, adding extra administrative burdens. We streamline these processes, so you can focus on patient care." />
          <Feature icon="💰" title="Streamline Your Financials" desc="Billing, cash flow, and financial management shouldn't stand in the way of delivering quality care. Let us handle the complexities so you can focus on your patients." />
          <Feature icon="🩺" title="Patient Access" desc="Eliminate manual paperwork and redundant data entry with streamlined digital solutions that simplify the registration process." />
        </div>
      </div>
    </section>
  );
};

/* =================== SECTION HEADING =================== */
const SectionHeading = ({ eyebrow, title }) => (
  <div className="bf-section-heading">
    {eyebrow && <p className="bf-eyebrow">{eyebrow}</p>}
    <h2>{title}</h2>
  </div>
);

/* =================== OUTCOMES =================== */
const Outcomes = () => (
  <section className="bf-outcomes">
    <div className="bf-outcomes-inner">
      <h2 className="bf-outcomes-heading">Outcomes That Resonate</h2>
      <p className="bf-outcomes-subtext">
        Our solutions are engineered to drive measurable impact and significantly elevate
        performance for our hospital partners.
      </p>
      <div className="bf-outcomes-cards">
        <OutcomeCard value="60%" title="Reduction" sub="In Accounts Receivable" />
        <OutcomeCard value="$10M" title="Collections" sub="Recovered Revenue" />
        <OutcomeCard value="24%" title="Achievements" sub="In Reimbursements" />
      </div>
    </div>
  </section>
);

const OutcomeCard = ({ value, title, sub }) => (
  <div className="bf-outcome-card">
    <h3>{value}</h3>
    <span>{title}</span>
    <p>{sub}</p>
  </div>
);

/* =================== TESTIMONIALS =================== */
const Testimonials = ({ lightPanel }) => {
  const testimonials = [
    {
      quote:
        "Partnering with MedRCM transformed our billing process. They quickly identified coding gaps, streamlined claims for both Vision and medical services, and ensured accurate payor routing using modifiers 25, 59, RT, LT, E2, E4. Since then, our clean claim rate has risen above 98%, and reimbursement times have improved dramatically.",
      author:
        "Head of Ophthalmology for a large Hospital based out of Bronx, NY",
    },
    {
      quote:
        "MedRCM has profoundly elevated our revenue cycle efficiency. Their nuanced understanding of E/M coding, Medicare guidelines, and value-based care billing has significantly improved our clean claim rate and reduced AR days. . Their proactive audits, real-time feedback, and front office training have streamlined both our operations and patient collections.",
      author:
        "Billing Manager of a large Internal Medicine Group Practice based out of NY",
    },
    {
      quote:
        "As a cardiovascular facility, we sincerely appreciate the professionalism and efficiency with which the MedRCM team addresses our patients' billing concerns. Many of our regular patients have expressed gratitude for the prompt and clear resolutions provided. . Your dedicated support in AR, billing, and coding has significantly streamlined our operations and enhanced patient satisfaction. Thank you!",
      author:
        "Office Manager of a large Cardiovascular Practice based out of IN",
    },
    {
      quote:
        "MedRCM has made a measurable difference in the way we manage billing. From their proactive communication to their strategic problem-solving, they’ve helped us avoid late filings, reduce AR, and adapt smoothly to growing operational demands — including the addition of new Providers. What stands out most is their ability to anticipate issues and implement practical solutions before they affect our bottom line.",
      author:
        "Office Manager at a Sleep Management Practice based out of CA",
    },
    {
      quote:
        "MedRCM brought structure, speed, and accuracy to our prior authorization process. Their team worked closely with us to build a workflow that fits our clinic’s needs, resulting in faster turnaround times and fewer issues.We also now benefit from clear, reliable financial reports that support smarter decision-making. MedRCM continues to deliver both operational improvements and real financial impact.",
      author:
        "Office Manager at a large Pain Management Group Practice based out of OH",
    },
  ];

  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % testimonials.length);
    }, 4500);
    return () => clearInterval(id);
  }, [testimonials.length]);

  return (
    <section className="bf-testimonials" style={{ backgroundColor: lightPanel }}>
      <div className="bf-test-inner">
        <SectionHeading title="What Hospitals Say About Us" />

        <div className="bf-test-card" key={index}>
          <div className="bf-test-quote">“{testimonials[index].quote}”</div>
          <div className="bf-test-author">{testimonials[index].author}</div>
        </div>

        <div className="bf-test-dots">
          {testimonials.map((_, i) => (
            <button
              key={i}
              className={`bf-dot ${i === index ? "active" : ""}`}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

/* =================== CONTACT FORM =================== */
/* =================== CONTACT FORM =================== */
const ContactForm = ({ primary }) => {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    hospital: "",
    facilityType: "",
    query: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch("http://localhost:5000/submit-form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pageName: "Business Finance Page",
          ...formData,
        }),
      });

      const result = await response.json();
      if (result.success) {
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 3000);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <section className="bf-contact">
      <div className="bf-contact-inner">

        <div className="bf-contact-left">
          <h3>Want to boost your hospital's performance?</h3>
          <p>
            Reach out to our specialists and see how our decades of experience
            can transform your operations with tailored solutions for your
            organization.
          </p>
        </div>

        <form className="bf-form" onSubmit={handleSubmit}>
          <div className="bf-row">
            <input
              placeholder="Name *"
              name="name"
              required
              onChange={handleChange}
            />
            <input
              placeholder="Email *"
              type="email"
              name="email"
              required
              onChange={handleChange}
            />
          </div>

          <div className="bf-row">
            <input
              placeholder="Phone"
              name="phone"
              onChange={handleChange}
            />
            <input
              placeholder="Hospital"
              name="hospital"
              onChange={handleChange}
            />
          </div>

          <div className="bf-row full">
            <select
              name="facilityType"
              required
              onChange={handleChange}
            >
              <option value="">Select a facility type</option>
              <option>Hospitals</option>
              <option>Group Practice</option>
              <option>Individual Provider</option>
            </select>
          </div>

          <div className="bf-row full">
            <textarea
              placeholder="Add any relevant information below"
              rows={5}
              name="query"
              onChange={handleChange}
            />
          </div>

          <button
            className="bf-btn full"
            type="submit"
            style={{ backgroundColor: primary }}
          >
            Schedule my demo
          </button>
        </form>
      </div>

      {/* SUCCESS POPUP */}
      {submitted && (
        <div className="modal-overlay">
          <div className="modal-box success-modal">
            <h3>Thank You!</h3>
            <p>Our specialist will contact you soon.</p>
          </div>
        </div>
      )}
    </section>
  );
};


/* =================== FOOTER =================== */
const Footer = () => (
  <footer className="bf-footer">
    <p>© 2025 MedRCM. All rights reserved.</p>
  </footer>
);

/* =================== MAIN PAGE =================== */
const BusinessFinance = ({ initialTheme = theme }) => {
  const [colors] = useState(initialTheme);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const openPopup = () => setIsPopupOpen(true);
  const closePopup = () => setIsPopupOpen(false);

  return (
    <>
      <Hero primary={colors.primary} accent={colors.accent} />
      <InfoPanel primary={colors.primary} openPopup={openPopup} />

      <section className="bf-info-panel bf-secondary">
        <div className="bf-container">
          <div className="bf-left">
            <h2 className="bf-title bf-big-title">Empowering Strategic Leadership</h2>
          </div>

          <div className="bf-right">
            <p className="bf-desc bf-tight">
              Our platform equips practice administrators with actionable insights into patient
              outcomes, quality metrics, and compliance—fostering informed decisions that nurture
              a culture of excellence and innovation in care.
            </p>

            <button className="bf-talk-btn" onClick={openPopup}>
              Let’s Talk
            </button>
          </div>
        </div>
      </section>

      <Outcomes />
      <Testimonials lightPanel={colors.lightPanel} />
      <ContactForm primary={colors.primary} />
      <Footer />

      <ContactPopup
        open={isPopupOpen}
        onClose={closePopup}
        pageName="Business Finance Page"
      />
    </>
  );
};

export default BusinessFinance;
